/**
 * `<SiteFooter>` — THE unified site footer (Phase 3, shell unification).
 *
 * Replaces both previous shells:
 *   - `components/layout/footer.tsx` (`Footer`) — old app/report footer
 *   - `components/marketing/footer.tsx` (`MarketingFooter`) — marketing footer
 * Both modules now re-export this component so every page — logged-in
 * and logged-out — renders identical footer markup.
 *
 * Structure per the Phase 3 spec: Product / Company / Resources columns
 * + a legal line. The link set is the UNION of both old footers so no
 * previously-linked route loses its footer entry. The "BETA · Survey"
 * link relocated here (Resources) from the old navbar badge.
 *
 * Server-component-friendly — no hooks; `CookieSettingsLink` is the
 * only client island.
 */

import Link from "next/link";
import { Logo } from "@/components/shared/logo";
import { CookieSettingsLink } from "./cookie-settings-link";

const BETA_SURVEY_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSdPPaWPrE95XX6kPXSehkgyYiyJSkD4L_tIciujWijBwZTK7g/viewform?usp=header";

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

const COLUMNS: Array<{ heading: string; links: FooterLink[] }> = [
  {
    heading: "Product",
    links: [
      { label: "Compass", href: "/compass" },
      // Marketing page about the stock-report product; the in-app
      // report browser stays linked right below.
      { label: "Reports", href: "/reports" },
      { label: "Browse stocks", href: "/browse" },
      { label: "Mutual Funds", href: "/funds" },
      { label: "Simulate", href: "/simulate" },
      { label: "Industry", href: "/industry" },
      { label: "Stock of the Week", href: "/stock-of-the-week" },
      { label: "Pricing", href: "/pricing" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Methodology", href: "/methodology" },
      { label: "Changelog", href: "/changelog" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    heading: "Resources",
    links: [
      // Public sample briefs live on the Compass marketing page —
      // /compass/briefs is the auth-gated personal-briefs dashboard.
      { label: "Sample briefs", href: "/compass#sample-briefs" },
      { label: "Glossary", href: "/glossary" },
      { label: "Blog", href: "/blog" },
      { label: "FAQ", href: "/faq" },
      { label: "Disclosures", href: "/disclaimer" },
      // Relocated from the old navbar "BETA · Survey" badge (Phase 3).
      { label: "Beta survey", href: BETA_SURVEY_URL, external: true },
    ],
  },
];

export function SiteFooter() {
  return (
    <footer className="border-t border-surface-light/30 bg-navy mt-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-1">
            <Logo />
            <p className="text-dim text-[12px] leading-relaxed mt-3 max-w-[220px]">
              An AI research analyst, working 24/7 on the stocks you care
              about.
            </p>
            {/* Social */}
            <ul className="flex flex-col gap-2 mt-4 list-none p-0 m-0">
              <li>
                <a
                  href="https://x.com/AnujScribble"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dim text-[12px] hover:text-foreground transition no-underline"
                >
                  Twitter / X
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/whatsthemoat_wtm/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dim text-[12px] hover:text-foreground transition no-underline"
                >
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href="mailto:admin@zoodleme.com"
                  className="text-dim text-[12px] hover:text-foreground transition no-underline"
                >
                  admin@zoodleme.com
                </a>
              </li>
            </ul>
          </div>

          {/* Product / Company / Resources columns */}
          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <div className="text-dim text-[10px] uppercase tracking-widest mb-3">
                {col.heading}
              </div>
              <ul className="space-y-2 list-none p-0 m-0">
                {col.links.map((link) =>
                  link.external ? (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground text-[13px] hover:text-foreground transition no-underline"
                      >
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className="text-muted-foreground text-[13px] hover:text-foreground transition no-underline"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Legal line */}
        <div className="border-t border-surface-light/20 pt-5 flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-dim text-[11px]">
              &copy; {new Date().getFullYear()} WhatsTheMoat. All rights
              reserved.
            </span>
            <Link
              href="/terms"
              className="text-dim text-[11px] hover:text-foreground transition no-underline"
            >
              Terms
            </Link>
            <Link
              href="/privacy"
              className="text-dim text-[11px] hover:text-foreground transition no-underline"
            >
              Privacy
            </Link>
            <CookieSettingsLink />
          </div>
          <span className="text-dim text-[11px] max-w-[480px]">
            WTM provides AI-generated research for educational and
            informational purposes only. Not investment advice.
          </span>
        </div>
      </div>
    </footer>
  );
}
